"use client";

/**
 * Portals overlay UI into `.mobile-layout-container` — the position:fixed
 * inset-0 element that provably fills the screen in this WebView.
 *
 * ⚠️ WHY: viewport-anchored `position: fixed` is unreliable inside the app's
 * WKWebView — body-level fixed elements (including Radix portals) rendered at
 * phantom offsets all day. The chrome that always rendered correctly (glass
 * dock, header) anchors inside the layout container instead. So sheets do the
 * same: portal INTO it and use `absolute` — the container IS the viewport.
 */
import { useRef } from "react";
import { createPortal } from "react-dom";

export function SheetPortal({ children }: { children: React.ReactNode }) {
  // ⚠️ Pin the host node instead of re-querying every render. The container
  // is a REACT-MANAGED div — HMR and layout remounts REPLACE it, and a
  // per-render querySelector then hands React a different host, forcing it
  // to re-parent the portal out of the old detached node. That's the
  // "null is not an object (deletedFiber.parentNode.removeChild)" crash.
  // Re-resolve only if our pinned host has left the document entirely.
  const hostRef = useRef<Element | null>(null);
  if (typeof document === "undefined") return null;
  if (!hostRef.current || !hostRef.current.isConnected) {
    hostRef.current = document.querySelector(".mobile-layout-container") ?? document.body;
  }
  return createPortal(children, hostRef.current);
}
